import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Users, TrendingUp, Radar, Video, ShieldCheck, LineChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { AdvertiseHero } from '@/components/advertise/AdvertiseHero';
import { AdvertiseBenefitsGrid } from '@/components/advertise/AdvertiseBenefitsGrid';
import { AdvertiseFAQ } from '@/components/advertise/AdvertiseFAQ';
import { ADVERTISE_AGENTS } from '@/constants/testIds/advertise';

const BENEFITS = [
  { icon: TrendingUp, title: 'Sort-to-top placement', desc: 'Your listings surface first in search results and map view within your markets.' },
  { icon: Radar, title: 'Buyer retargeting', desc: 'Bring shoppers back with retargeting ads across the web after they view your listing.' },
  { icon: Video, title: '3D & Matterport tours', desc: 'Immersive walkthroughs keep serious buyers engaged 24/7, even off-hours.' },
  { icon: ShieldCheck, title: 'Vetted buyer leads', desc: 'Every inquiry is screened before it reaches your inbox — no diverted leads.' },
  { icon: Users, title: 'Team & brokerage tools', desc: 'Manage multiple agents, listings, and lead routing from one dashboard.' },
  { icon: LineChart, title: 'Performance analytics', desc: 'See impressions, clicks, saves, and leads in real time.' },
];

const FAQS = [
  ['What\u2019s included in Agent Membership?', 'Sort-to-top placement across your active listings, retargeting ads, Matterport support, vetted lead delivery, and a performance dashboard — billed annually.'],
  ['What is Listing Boost for agents?', 'A one-time purchase that boosts a single listing without a full membership — ideal if you only need extra exposure on one property.'],
  ['Do I need an MLS ID to sign up?', 'No — it\u2019s optional and helps us sync your active listings faster, but you can get started without one.'],
  ['How soon will someone contact me?', 'Our team reaches out within 1 business day of your request, or at the demo time you pick.'],
];

export default function AgentsLanding() {
  const revealRef = useScrollReveal();
  return (
    <div ref={revealRef}>
      <AdvertiseHero
        eyebrow={<><Sparkles size={13} className="mr-1 inline" />For real estate agents</>}
        title="Sort to the top of every search buyers run"
        subtitle="Agent Membership and Listing Boost put your listings in front of ready-to-move buyers with premium placement, retargeting, and vetted leads."
        primaryCta={{ label: 'Get started', to: '/advertise/agents/plan', testId: ADVERTISE_AGENTS.landingCta }}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="reveal grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Agent Membership</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">For agents & brokerages</h3>
            <p className="mt-2 text-sm text-slate-600">Ongoing premium placement across all of your active listings, plus retargeting, analytics, and vetted leads.</p>
            <Button asChild className="mt-5 rounded-full"><Link to="/advertise/agents/plan">Explore Membership<ArrowUpRight size={16} className="ml-1" /></Link></Button>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Listing Boost</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">For a single listing</h3>
            <p className="mt-2 text-sm text-slate-600">Boost one active listing without committing to a full membership — great for a hot property that needs extra push.</p>
            <Button asChild variant="outline" className="mt-5 rounded-full"><Link to="/advertise/agents/plan">Explore Listing Boost<ArrowUpRight size={16} className="ml-1" /></Link></Button>
          </div>
        </div>
      </section>

      <section className="bg-sky-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center">
            <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Everything you need to sell faster</h2>
          </div>
          <div className="mt-8"><AdvertiseBenefitsGrid items={BENEFITS} /></div>
        </div>
      </section>

      <AdvertiseFAQ items={FAQS} />
    </div>
  );
}
